import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Card, Progress, Space, Tag, Typography, message } from 'antd';
import { ingestionStore } from '../store/IngestionStore';
import { ingestionApi } from '../ingestionApi';
import { fileNameOf, formatBytes, sortedVariants, variationLabel } from '../ingestionHelpers';
import { VariantAssetViewer } from './VariantAssetViewer';
import { assetSpec, compressionSourceOf } from './Step4Assets';

const { Text } = Typography;

const savingPct = (original, compressed) =>
    original && compressed ? Math.max(0, Math.round((1 - compressed / original) * 100)) : 0;

export const Step5Compress = observer(({ item, refreshTick }) => {
    const [summary, setSummary] = useState(null);
    const [byVariant, setByVariant] = useState({});

    const load = async () => {
        try {
            const [compression, assetRows] = await Promise.all([
                ingestionApi.getCompression(item.id),
                ingestionApi.getAssets(item.id),
            ]);
            setSummary(compression);
            setByVariant(Object.fromEntries(assetRows.map((r) => [r.variantId, r])));
        } catch (e) {
            message.error(e.message || 'Failed to load compression');
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.id, refreshTick]);

    useEffect(() => {
        if (item.status !== 'running') return undefined;
        const interval = setInterval(() => {
            ingestionStore.refreshCurrentItem(item.id);
            load();
        }, 4000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.status, item.id]);

    const spec = assetSpec(item.itemType, item.rugClass);
    const sizeOf = (variantId) => (summary?.variants || []).find((v) => v.variantId === variantId) || {};

    const handleDownload = async (asset, variantId) => {
        try {
            const blob = await ingestionApi.getAssetBlob(item.id, asset.id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileNameOf(asset, variantId);
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            message.error(e.message || 'Download failed');
        }
    };

    const handleRunStep = async () => {
        try {
            await ingestionStore.runStep(item.id, 5);
            message.success('Compression queued');
            await ingestionStore.refreshCurrentItem(item.id);
            await load();
        } catch (e) {
            message.error(e.message || 'Failed to run compression');
        }
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Text strong>{summary ? `Total compressed: ${formatBytes(summary.totalCompressedBytes)}` : ''}</Text>
                    <Button
                        type="primary"
                        loading={ingestionStore.runningStep || item.status === 'running'}
                        onClick={handleRunStep}
                    >
                        Run compression
                    </Button>
                </Space>
                {summary && (
                    <div>
                        {summary.over3mb && (
                            <Alert
                                style={{ marginTop: 12 }}
                                type="warning"
                                showIcon
                                message="A variant's compressed asset exceeds 3 MB"
                                description="Each variant loads on its own, so the limit is per-variant. Large variants (flagged below) load slowly in the editor — consider re-uploading a lighter model before publishing."
                            />
                        )}
                    </div>
                )}
            </Card>

            {sortedVariants(item).map((variant) => {
                const size = sizeOf(variant.id);
                // Only what this step touches: the original it compresses and its output.
                const all = byVariant[variant.id]?.assets || [];
                const source = compressionSourceOf(spec, all);
                const assets = [...all.filter((a) => a.kind === 'compressed'), ...(source ? [source] : [])];
                return (
                    <Card
                        key={variant.id}
                        size="small"
                        title={variationLabel(item, variant)}
                        extra={
                            size.compressedBytes ? (
                                <Space>
                                    <Text type="secondary">
                                        {formatBytes(size.originalBytes)} → {formatBytes(size.compressedBytes)}
                                    </Text>
                                    {size.over3mb ? (
                                        <Tag color="error">&gt; 3 MB</Tag>
                                    ) : (
                                        <Tag color="success">−{savingPct(size.originalBytes, size.compressedBytes)}%</Tag>
                                    )}
                                </Space>
                            ) : (
                                <Tag color="warning">not compressed</Tag>
                            )
                        }
                    >
                        {size.compressedBytes != null && (
                            <Progress
                                percent={savingPct(size.originalBytes, size.compressedBytes)}
                                size="small"
                                status="active"
                                style={{ marginBottom: 12 }}
                            />
                        )}
                        <VariantAssetViewer
                            itemId={item.id}
                            variantId={variant.id}
                            assets={assets}
                            itemType={item.itemType}
                            assetsOnly
                            emptyText="Nothing to compress yet"
                            onDownload={(a) => handleDownload(a, variant.id)}
                        />
                    </Card>
                );
            })}
        </Space>
    );
});
